import {
  Box,
  Button,
  FlexBox,
  FormField as FormFieldComponent,
  FormGrid,
  IconButton,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { useEffect } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import {
  HookFormSelectionList,
  HookFormSwitch,
  HookFormText,
} from 'components/hookForm';

import {
  CustomFormField,
  CustomFormFieldType,
} from '../../../../../../../customForms.types';
import { getHumanReadableFormFieldType } from '../../../../../utils/humanReadableFormFieldType';

type FormMode = 'add' | 'edit';

interface Props {
  isOpen: boolean;
  activeFormField: CustomFormField | null;
  fieldTypes: CustomFormFieldType[];
  onClose: () => void;
  onSave: (field: CustomFormField) => void;
}

// replace the type attribute with a list of types to satisfy the selection list
type FormValues = Omit<CustomFormField, 'type'> & {
  type: [CustomFormFieldType];
};

const modalTitle: Record<FormMode, string> = {
  add: 'Add new field',
  edit: 'Edit field',
};

const actionButtonLabel: Record<FormMode, string> = {
  add: 'Add',
  edit: 'Save',
};

export function FieldDetailsModal({
  activeFormField,
  fieldTypes,
  isOpen,
  onClose,
  onSave,
}: Props): JSX.Element {
  const mode = activeFormField ? 'edit' : 'add';

  const { handleSubmit, control, reset, formState } = useForm<FormValues>({
    values: {
      id: activeFormField?.id ?? '',
      title: activeFormField?.title ?? '',
      placeholder: activeFormField?.placeholder ?? '',
      required: activeFormField?.required ?? false,
      type: activeFormField?.type ? [activeFormField?.type] : ['text'],
      options: activeFormField?.options ?? [],
    },
  });

  const {
    append,
    fields: optionsFields,
    remove,
  } = useFieldArray({ control, name: 'options' });

  const values = useWatch<FormValues>({ control });
  const fieldTypeValue = values.type?.[0];

  useEffect(() => {
    if (optionsFields.length === 0) {
      append({ value: '' });
    }
  }, [append, optionsFields.length]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const submitHandler = handleSubmit((values) => {
    const fieldType = values.type[0];
    onSave({
      id: mode === 'add' ? uuid() : (activeFormField?.id as string),
      title: values.title,
      placeholder: values.placeholder,
      required: values.required,
      type: fieldType,
      options: fieldType === 'select' ? values.options : undefined,
    });

    handleClose();
  });

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Box width="700px">
        <ModalHead title={modalTitle[mode]} onClose={handleClose} />
        <form onSubmit={submitHandler}>
          <ModalBody>
            <FormGrid>
              <FormFieldComponent>
                <HookFormSelectionList
                  name="type"
                  label="Field type"
                  control={control}
                  componentProps={{
                    id: 'type',
                    getItemTitleByValue: getHumanReadableFormFieldType,
                  }}
                >
                  {fieldTypes.map((fieldType) => (
                    <SelectionList.Item key={fieldType} value={fieldType}>
                      {getHumanReadableFormFieldType(fieldType)}
                    </SelectionList.Item>
                  ))}
                </HookFormSelectionList>

                <HookFormText
                  name="title"
                  label="Title"
                  control={control}
                  rules={{
                    required: { value: true, message: 'Title is required' },
                  }}
                  componentProps={{
                    id: 'title',
                    placeholder: 'Enter a title for the field',
                  }}
                />
              </FormFieldComponent>

              <FormFieldComponent>
                <HookFormText
                  name="placeholder"
                  label="Placeholder"
                  control={control}
                  componentProps={{
                    id: 'placeholder',
                    placeholder: 'Enter a placeholder for the field',
                  }}
                />
              </FormFieldComponent>

              {fieldTypeValue === 'select' && (
                <FormFieldComponent>
                  <FlexBox flexDirection="column" gap="200">
                    <Text.Paragraph variant="3">Options</Text.Paragraph>

                    <Text.Paragraph variant="4" subdued>
                      Include the options that you would like to appear in the
                      dropdown menu.
                    </Text.Paragraph>

                    <Box>
                      {optionsFields.map((option, index) => (
                        <FlexBox
                          key={option.id}
                          gap="200"
                          alignItems="baseline"
                        >
                          <Text.Heading variant="heading-4">
                            {index + 1}
                          </Text.Heading>
                          <HookFormText
                            name={`options.${index}.value`}
                            control={control}
                            noLabel
                            rules={{
                              required: { value: true, message: 'required' },
                            }}
                            componentProps={{
                              id: `options.${index}.value`,
                              placeholder: 'Enter an option',
                            }}
                          />
                          {index > 0 && (
                            <IconButton
                              type="button"
                              onClick={() => remove(index)}
                              icon="trash"
                              variant="secondary"
                            />
                          )}
                        </FlexBox>
                      ))}
                    </Box>

                    <Button
                      type="button"
                      onClick={() => append({ value: '' })}
                      icon="plus"
                      variant="secondary"
                      style={{ alignSelf: 'flex-start' }}
                    >
                      Add new option
                    </Button>
                  </FlexBox>
                </FormFieldComponent>
              )}

              <FormFieldComponent>
                <FlexBox alignItems="center" gap="300">
                  <HookFormSwitch name="required" control={control} />
                  <FlexBox flexDirection="column">
                    <Text.Paragraph variant="3">Required</Text.Paragraph>
                    <Text.Paragraph variant="4" subdued>
                      The user will be required to fill out all the fields
                      marked as required (*) in the form, otherwise an alert
                      will be shown.
                    </Text.Paragraph>
                  </FlexBox>
                </FlexBox>
              </FormFieldComponent>
            </FormGrid>
          </ModalBody>

          <ModalFoot
            cancelOnClick={handleClose}
            confirmName={actionButtonLabel[mode]}
            confirmOnClick={submitHandler}
            confirmProps={{ type: 'submit', disabled: !formState.isDirty }}
          />
        </form>
      </Box>
    </Modal>
  );
}
